import React from 'react';
import { ClipboardList, EyeOff, Eye, X } from 'lucide-react';

/**
 * Initial pop-up when loading a listing.
 * Three options: Appraisal | Off-Market Listing | On-Market Listing.
 */
const PropertyTypeModal = ({ isOpen, onClose, onSelectType }) => {
  if (!isOpen) return null;

  const handleSelect = (type) => {
    onSelectType(type);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full animate-fadeIn">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Choose Listing Type</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content - 3 options */}
        <div className="p-8">
          <p className="text-gray-600 mb-8 text-center">
            Select whether this is an Appraisal, Off-Market Listing, or On-Market Listing
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Appraisal */}
            <button
              onClick={() => handleSelect('appraisal')}
              className="group relative overflow-hidden rounded-xl border-2 border-gray-200 hover:border-amber-500 transition-all duration-300 hover:shadow-lg"
            >
              <div className="p-6 text-center">
                <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-3 group-hover:bg-amber-200 transition-colors">
                  <ClipboardList className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Appraisal</h3>
                <p className="text-xs text-gray-600">
                  Saves to your pipeline as an appraisal. Choose off-market or on-market and Cold / Warm / Hot for sorting.
                </p>
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 to-amber-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
            </button>

            {/* Off-Market Listing */}
            <button
              onClick={() => handleSelect('offmarket')}
              className="group relative overflow-hidden rounded-xl border-2 border-gray-200 hover:border-purple-500 transition-all duration-300 hover:shadow-lg"
            >
              <div className="p-6 text-center">
                <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-3 group-hover:bg-purple-200 transition-colors">
                  <EyeOff className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Off-Market Listing</h3>
                <p className="text-xs text-gray-600">
                  Private listing, only shared with selected buyers. No public visibility options.
                </p>
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-400 to-purple-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
            </button>

            {/* On-Market Listing */}
            <button
              onClick={() => handleSelect('onmarket')}
              className="group relative overflow-hidden rounded-xl border-2 border-gray-200 hover:border-teal-500 transition-all duration-300 hover:shadow-lg"
            >
              <div className="p-6 text-center">
                <div className="mx-auto w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-3 group-hover:bg-teal-200 transition-colors">
                  <Eye className="w-8 h-8 text-teal-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">On-Market Listing</h3>
                <p className="text-xs text-gray-600">
                  Public listing with visibility options (Buyer Portal, REA, Domain, Collings.com.au, etc.).
                </p>
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 to-teal-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-4 bg-gray-50 border-t border-gray-200 rounded-b-2xl">
          <p className="text-xs text-gray-500 text-center">
            You can change visibility later in property details (on-market only).
          </p>
        </div>
      </div>
    </div>
  );
};

export default PropertyTypeModal;
